import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, LogOut, Save, ExternalLink, Edit3, MessageSquare, Star, Send, Sparkles, Flag, Loader, MousePointerClick, TrendingUp, TrendingDown, Monitor, RotateCcw, RefreshCw, Clock, Image as ImageIcon, Trash2, Upload, GripVertical, Plus, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import PlansEditor from "@/components/PlansEditor";
import MarkdownEditor from "@/components/MarkdownEditor";
import {
  publisherClient as apiClient,
  clearPublisherToken,
  publisherListMedia,
  publisherUploadImage,
  publisherCreateMedia,
  publisherUpdateMedia,
  publisherDeleteMedia,
  publisherReorderMedia,
} from "@/lib/publisherApi";
import { mediaUrl, getFeatureTaxonomy } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublisherDashboardPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, email: "", apps: [], error: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saveMsg, setSaveMsg] = useState("");
  // Bump this to force the live-preview iframe to reload (after save).
  const [previewVersion, setPreviewVersion] = useState(0);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/publisher/me");
      setState({ loading: false, email: data.email, apps: data.apps || [], error: "" });
    } catch (e) {
      setState({ loading: false, email: "", apps: [], error: e?.response?.status === 401 ? "unauthorized" : "fetch_error" });
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    document.title = isNl ? "Publisher Dashboard | HorseAppCompare" : "Publisher Dashboard | HorseAppCompare";
  }, [isNl]);

  const startEdit = (app) => {
    setEditing(app.slug);
    setForm({
      tagline: app.tagline || "",
      description: app.description || "",
      website: app.website || "", logo: app.logo || "",
      features: Array.isArray(app.features) ? [...app.features] : [],
      plans: Array.isArray(app.plans) ? app.plans.map((p) => ({ ...p, features: [...(p.features || [])] })) : [],
    });
    setSaveMsg("");
  };

  const save = async () => {
    setSaveMsg(isNl ? "Bezig met opslaan..." : "Saving...");
    try {
      await apiClient.patch(`/publisher/apps/${editing}`, form);
      setSaveMsg("✅ " + (isNl ? "Opgeslagen — preview verversen…" : "Saved — refreshing preview…"));
      setPreviewVersion((v) => v + 1);
      setTimeout(() => { setEditing(null); load(); }, 1200);
    } catch (e) {
      setSaveMsg("❌ " + (e?.response?.data?.detail || "Save failed"));
    }
  };

  const logout = async () => {
    try { await apiClient.post("/publisher/logout"); } catch (_) { /* noop */ }
    clearPublisherToken();
    navigate("/");
  };

  if (state.loading) return <main className="hac-container py-20 text-center"><Loader2 className="animate-spin mx-auto text-[#2563EB]" /></main>;

  if (state.error === "unauthorized") {
    return (
      <main className="hac-container py-20 max-w-md text-center" data-testid="publisher-unauthorized">
        <ShieldCheck size={48} className="mx-auto text-[#94A3B8] mb-4" />
        <h1 className="font-heading text-3xl text-[#0F172A] mb-2">{isNl ? "Niet ingelogd" : "Not logged in"}</h1>
        <p className="text-[#64748B] mb-6">
          {isNl
            ? "Vraag een eenmalige inloglink aan via je geverifieerde e-mailadres, of claim eerst een app."
            : "Request a one-time sign-in link with your verified email, or claim an app first."}
        </p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/publisher/login" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm" data-testid="publisher-login-cta">
            {isNl ? "Inloggen" : "Sign in"}
          </Link>
          <Link to="/apps" className="border border-[#E2E8F0] hover:border-[#94A3B8] text-[#0F172A] px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm">
            {isNl ? "Bekijk apps" : "Browse apps"}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="hac-container py-12 max-w-5xl" data-testid="publisher-dashboard">
      <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
            <ShieldCheck size={12} /> {isNl ? "Geverifieerd publisher" : "Verified publisher"}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl text-[#0F172A]">{isNl ? "Publisher dashboard" : "Publisher dashboard"}</h1>
          <p className="text-[#64748B] mt-1 text-sm truncate">{state.email}</p>
        </div>
        <button onClick={logout} className="shrink-0 whitespace-nowrap text-sm text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid="publisher-logout"><LogOut size={14} /> {isNl ? "Uitloggen" : "Log out"}</button>
      </div>

      {state.apps.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center">
          <p className="text-[#64748B]">{isNl ? "Geen apps op dit account." : "No apps on this account."}</p>
        </div>
      ) : (
        <>
          <ActionCenter
            apps={state.apps}
            isNl={isNl}
            apiClient={apiClient}
            onEdit={(app) => { startEdit(app); window.scrollTo({ top: document.querySelector(`[data-testid="publisher-app-${app.slug}"]`)?.offsetTop || 0, behavior: "smooth" }); }}
          />
          <div className="space-y-4">
          {state.apps.map((app) => (
            <div key={app.slug} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`publisher-app-${app.slug}`}>
              {editing === app.slug ? (
                <div className="space-y-3 min-w-0">
                  <h3 className="font-bold text-lg text-[#0F172A] mb-3">{app.name}</h3>
                  <div>
                    <label className="overline block mb-1">Tagline</label>
                    <input className="hac-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} maxLength={150} placeholder="One-line summary (English)" />
                    <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "Schrijf in het Engels — we vertalen automatisch naar 22 talen." : "Write in English — we'll auto-translate to 22 languages."}</p>
                  </div>
                  <div><label className="overline block mb-1">Description</label><MarkdownEditor value={form.description} onChange={(v) => setForm({ ...form, description: v })} maxLength={1500} placeholder="Use **bold**, *italic*, lists and links to write a richer description (English)." testId="publisher-desc-en" /></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="overline block mb-1">Website</label><input className="hac-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
                    <LogoField slug={app.slug} value={form.logo} onChange={(logo) => setForm({ ...form, logo })} isNl={isNl} />
                  </div>
                  <div className="border-t border-[#E2E8F0] pt-4">
                    <h4 className="font-bold text-[#0F172A] mb-1">{isNl ? "Functionaliteiten" : "Features"}</h4>
                    <p className="text-xs text-[#64748B] mb-3">{isNl ? "Selecteer alle modules die jouw app aanbiedt. Dit bepaalt waar je app verschijnt in categorieën en filters." : "Tick every module your app offers. This decides which categories and filters your app shows up in."}</p>
                    <FeaturesEditor value={form.features || []} onChange={(features) => setForm({ ...form, features })} isNl={isNl} />
                  </div>
                  <div className="border-t border-[#E2E8F0] pt-4">
                    <h4 className="font-bold text-[#0F172A] mb-1">{isNl ? "Schermafbeeldingen" : "Screenshots"}</h4>
                    <p className="text-xs text-[#64748B] mb-3">{isNl ? "Upload tot 12 screenshots. Listings met 3+ screenshots krijgen gemiddeld 2,3× meer kliks." : "Upload up to 12 screenshots. Listings with 3+ screenshots get on average 2.3× more clicks."}</p>
                    <ScreenshotsManager slug={app.slug} isNl={isNl} onChange={() => setPreviewVersion((v) => v + 1)} />
                  </div>
                  <div className="border-t border-[#E2E8F0] pt-4">
                    <h4 className="font-bold text-[#0F172A] mb-1">{isNl ? "Prijzen & modules" : "Pricing & modules"}</h4>
                    <p className="text-xs text-[#64748B] mb-3">{isNl ? "Voeg jouw plannen toe (bv. Gratis, Pro, Team). Per plan: naam, valuta (EUR/USD/GBP), maand- en jaarprijs, omschrijving en welke functionaliteiten erin zitten." : "Add your pricing tiers (e.g. Free, Pro, Team). For each tier: name, currency (EUR/USD/GBP), monthly and yearly price, description and which features are included."}</p>
                    <PlansEditor value={form.plans || []} onChange={(plans) => setForm({ ...form, plans })} isNl={isNl} />
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 rounded-full text-sm inline-flex items-center gap-1.5" data-testid="publisher-save"><Save size={14} /> {isNl ? "Opslaan" : "Save"}</button>
                    <button onClick={() => setEditing(null)} className="text-sm text-[#64748B] hover:text-[#0F172A]">{isNl ? "Annuleren" : "Cancel"}</button>
                    {saveMsg && <span className="text-sm ml-2">{saveMsg}</span>}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold shrink-0" style={{ backgroundColor: app.color || "#2563EB" }}>{app.name.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#0F172A]">{app.name} {!app.active && <span className="text-xs text-red-600 ml-2">({isNl ? "inactief" : "inactive"})</span>}</h3>
                      <p className="text-sm text-[#64748B] truncate">{isNl && app.tagline_nl ? app.tagline_nl : app.tagline}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link to={`/apps/${app.slug}`} className="text-xs text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1"><ExternalLink size={12} /> {isNl ? "Bekijk" : "View"}</Link>
                      <button onClick={() => startEdit(app)} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1" data-testid={`publisher-edit-${app.slug}`}><Edit3 size={12} /> {isNl ? "Bewerken" : "Edit"}</button>
                    </div>
                  </div>
                  <ContentHealth slug={app.slug} isNl={isNl} />
                  <FreshnessWidget app={app} isNl={isNl} onEdit={() => startEdit(app)} />
                  <ClickAnalytics slug={app.slug} isNl={isNl} />
                </div>
              )}
            </div>
          ))}
          </div>
        </>
      )}

      <PublisherReviewsSection isNl={isNl} apiClient={apiClient} />

      <div className="mt-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white text-center">
        <h2 className="font-heading text-xl mb-2">{isNl ? "Wil je een premium listing?" : "Want a premium listing?"}</h2>
        <p className="text-white/80 text-sm mb-4">{isNl ? "Verschijn bovenaan zoekresultaten, met gouden badge en directe call-to-action." : "Appear at the top of search results, with golden badge and direct call-to-action."}</p>
        <Link to="/premium-listing" className="bg-white text-amber-600 hover:text-amber-700 font-bold px-6 py-2.5 rounded-full text-sm inline-block">{isNl ? "Meer info" : "Learn more"}</Link>
      </div>
    </main>
  );
};

export default PublisherDashboardPage;

const PublisherReviewsSection = ({ isNl, apiClient }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState({});
  const [insightsLoading, setInsightsLoading] = useState({});

  const refresh = () => {
    setLoading(true);
    apiClient.get("/publisher/reviews")
      .then((r) => setData(r.data))
      .catch(() => setData({ reviews: [], nps: null, apps: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const submitReply = async (rid) => {
    setBusy(true);
    try {
      await apiClient.post(`/publisher/reviews/${rid}/reply`, { body: replyText });
      setReplyOpen(null); setReplyText(""); refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed";
      if (e?.response?.status === 402) alert(isNl ? "Reageren vereist een Premium listing." : "Replying requires a Premium listing.");
      else alert(msg);
    } finally { setBusy(false); }
  };

  const flagReview = async (rid) => {
    if (!window.confirm(isNl ? "Markeer voor moderator-review?" : "Flag for moderator review?")) return;
    try { await apiClient.post(`/publisher/reviews/${rid}/flag`); refresh(); } catch (_) {}
  };

  const runInsights = async (slug) => {
    setInsightsLoading((s) => ({ ...s, [slug]: true }));
    try {
      const { data: d } = await apiClient.post(`/publisher/ai-insights/${slug}`);
      setInsights((s) => ({ ...s, [slug]: d }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) alert(isNl ? "AI inzichten vereisen een Premium listing." : "AI insights require a Premium listing.");
      else if (status === 503) alert(isNl ? "AI niet geconfigureerd." : "AI not configured.");
      else alert(e?.response?.data?.detail || "Failed");
    } finally {
      setInsightsLoading((s) => ({ ...s, [slug]: false }));
    }
  };

  if (loading) return <div className="mt-10 text-center text-[#94A3B8]"><Loader2 className="animate-spin inline" /></div>;
  if (!data || data.reviews.length === 0) {
    return (
      <div className="mt-10 bg-white border border-[#E2E8F0] rounded-2xl p-6 text-center" data-testid="publisher-reviews-empty">
        <MessageSquare size={28} className="mx-auto text-[#94A3B8] mb-2" />
        <p className="text-sm text-[#64748B]">{isNl ? "Nog geen reviews op jouw apps." : "No reviews on your apps yet."}</p>
      </div>
    );
  }
  return (
    <section className="mt-10" data-testid="publisher-reviews-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-heading text-2xl text-[#0F172A]">{isNl ? "Reviews & feedback" : "Reviews & feedback"}</h2>
        {data.nps !== null && data.nps !== undefined && (
          <div className="text-xs text-[#64748B] inline-flex items-center gap-2">
            <span className="font-mono font-bold text-base text-[#0F172A]">NPS {data.nps > 0 ? `+${data.nps}` : data.nps}</span>
            <span>· {data.promoters} {isNl ? "promotors" : "promoters"} / {data.detractors} {isNl ? "detractors" : "detractors"} / {data.total_approved} {isNl ? "totaal" : "total"}</span>
          </div>
        )}
      </div>

      {/* AI insights per app */}
      {data.apps.map((app) => (
        <div key={app.slug} className="mb-4 bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4" data-testid={`publisher-insights-${app.slug}`}>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="font-bold text-[#0F172A] inline-flex items-center gap-1.5"><Sparkles size={14} className="text-violet-600" /> {isNl ? "AI inzichten" : "AI insights"} · {app.name}</div>
            <button onClick={() => runInsights(app.slug)} disabled={!!insightsLoading[app.slug]} className="bg-violet-600 hover:bg-violet-700 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-50" data-testid={`publisher-insights-run-${app.slug}`}>
              {insightsLoading[app.slug] ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {isNl ? "Genereer inzichten" : "Generate insights"}
            </button>
          </div>
          {insights[app.slug]?.insights && (
            <div className="grid md:grid-cols-2 gap-3 mt-2 text-xs">
              {["top_strengths", "top_weaknesses", "quick_wins", "roadmap_themes"].map((k) => (
                Array.isArray(insights[app.slug].insights[k]) && (
                  <div key={k} className="bg-white border border-violet-100 rounded-lg p-3">
                    <div className="font-bold text-violet-700 mb-1 uppercase text-[10px] tracking-widest">{k.replace(/_/g, " ")}</div>
                    <ul className="space-y-0.5 text-[#0F172A]">
                      {insights[app.slug].insights[k].map((item, i) => <li key={i}>• {item}</li>)}
                    </ul>
                  </div>
                )
              ))}
            </div>
          )}
          {insights[app.slug] && insights[app.slug].ok === false && (
            <p className="text-xs text-[#64748B]">{insights[app.slug].reason}</p>
          )}
        </div>
      ))}

      <div className="space-y-3">
        {data.reviews.map((r) => (
          <div key={r.id} className={`bg-white border rounded-xl p-4 ${r.status !== "approved" ? "border-amber-300 bg-amber-50/30" : "border-[#E2E8F0]"}`} data-testid={`publisher-review-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= r.rating ? "fill-amber-500 text-amber-500" : "text-[#CBD5E1]"} />)}</div>
                  <span className="text-xs text-[#64748B]">{r.author_name}</span>
                  <span className="text-xs text-[#94A3B8]">· {r.slug}</span>
                  {r.status !== "approved" && <span className="text-[10px] uppercase font-bold tracking-widest text-amber-700 bg-amber-200/60 px-1.5 py-0.5 rounded">{r.status}</span>}
                </div>
                {r.title && <h3 className="font-bold text-[#0F172A] text-sm">{r.title}</h3>}
                <p className="text-sm text-[#0F172A] mt-1 whitespace-pre-wrap">{r.body}</p>
              </div>
              <button onClick={() => flagReview(r.id)} className="text-[#CBD5E1] hover:text-amber-600 p-1" title={isNl ? "Markeer voor moderator" : "Flag for moderator"} data-testid={`publisher-flag-${r.id}`}><Flag size={12} /></button>
            </div>
            {(r.replies || []).length > 0 && (
              <div className="mt-3 ml-4 pl-3 border-l-4 border-[#2563EB] text-sm bg-[#EFF6FF] rounded-r-lg p-2">
                {r.replies.map((rep) => (
                  <div key={rep.id}><strong className="text-[#2563EB]">{rep.publisher_name}:</strong> {rep.body}</div>
                ))}
              </div>
            )}
            {(r.replies || []).length === 0 && replyOpen !== r.id && r.status === "approved" && (
              <button onClick={() => { setReplyOpen(r.id); setReplyText(""); }} className="mt-2 text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1" data-testid={`publisher-reply-open-${r.id}`}><MessageSquare size={11} /> {isNl ? "Reageer" : "Reply"}</button>
            )}
            {replyOpen === r.id && (
              <div className="mt-3">
                <textarea className="hac-input min-h-[80px]" placeholder={isNl ? "Bedank de reviewer, leg context uit, beloof verbeteringen…" : "Thank the reviewer, explain context, promise improvements…"} value={replyText} onChange={(e) => setReplyText(e.target.value)} maxLength={2000} data-testid={`publisher-reply-textarea-${r.id}`} />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => submitReply(r.id)} disabled={busy || replyText.length < 5} className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid={`publisher-reply-submit-${r.id}`}>{busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} {isNl ? "Plaats reactie" : "Post reply"}</button>
                  <button onClick={() => setReplyOpen(null)} className="text-xs text-[#64748B] hover:text-[#0F172A]">{isNl ? "Annuleren" : "Cancel"}</button>
                </div>
                <p className="text-[10px] text-[#94A3B8] mt-1">{isNl ? "Premium-only feature. Je reactie is publiek zichtbaar onder de review." : "Premium-only feature. Your reply is publicly visible under the review."}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};


// ---- Click analytics — outbound clicks per app ----
const WINDOW_OPTIONS = [
  { value: 7,  label_en: "7 days",  label_nl: "7 dagen" },
  { value: 30, label_en: "30 days", label_nl: "30 dagen" },
  { value: 90, label_en: "90 days", label_nl: "90 dagen" },
];

const SOURCE_LABELS = {
  directory: { en: "Directory", nl: "Directory" },
  ranking:   { en: "Top 100",   nl: "Top 100" },
  compare:   { en: "Compare pages", nl: "Vergelijkingen" },
  detail:    { en: "App detail", nl: "App-detail" },
  advisor:   { en: "AI Advisor", nl: "AI Advisor" },
  blog:      { en: "Blog",       nl: "Blog" },
  category:  { en: "Categories", nl: "Categorieën" },
  best:      { en: "Best-of lists", nl: "Best-of lijsten" },
  feature:   { en: "Feature pages", nl: "Feature-pagina's" },
  spotlight: { en: "Spotlight",  nl: "Spotlight" },
  trending:  { en: "Trending",   nl: "Trending" },
  ad:        { en: "Ads",        nl: "Advertenties" },
  direct:    { en: "Direct",     nl: "Direct" },
};

const ClickAnalytics = ({ slug, isNl }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient.get(`/publisher/apps/${slug}/clicks?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, days]);

  if (loading) {
    return (
      <div className="border-t border-[#E2E8F0] pt-4 text-center text-[#94A3B8] text-xs">
        <Loader2 size={14} className="animate-spin inline" /> {isNl ? "Klikstatistieken laden…" : "Loading click stats…"}
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border-t border-[#E2E8F0] pt-4 text-xs text-red-600">{error || "No data"}</div>
    );
  }

  const max = Math.max(1, ...data.daily.map((d) => d.clicks));
  const deltaPositive = (data.delta_pct ?? 0) >= 0;
  const DeltaIcon = deltaPositive ? TrendingUp : TrendingDown;

  return (
    <div className="border-t border-[#E2E8F0] pt-4" data-testid={`click-analytics-${slug}`}>
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MousePointerClick size={16} className="text-[#2563EB]" />
          <h4 className="font-bold text-sm text-[#0F172A]">{isNl ? "Klik-analytics" : "Click analytics"}</h4>
        </div>
        <div className="inline-flex bg-[#F1F5F9] rounded-full p-0.5">
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w.value}
              onClick={() => setDays(w.value)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${days === w.value ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"}`}
              data-testid={`click-window-${w.value}`}
            >
              {isNl ? w.label_nl : w.label_en}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3" data-testid={`stat-total-${slug}`}>
          <div className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold">{isNl ? "Totaal klikken" : "Total clicks"}</div>
          <div className="text-2xl font-bold text-[#0F172A] tabular-nums">{data.total}</div>
          {data.delta_pct !== null && data.delta_pct !== undefined && (
            <div className={`text-[10px] font-bold inline-flex items-center gap-0.5 mt-0.5 ${deltaPositive ? "text-emerald-700" : "text-red-700"}`}>
              <DeltaIcon size={10} /> {deltaPositive ? "+" : ""}{data.delta_pct}% {isNl ? "t.o.v. vorige periode" : "vs prev period"}
            </div>
          )}
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold">{isNl ? "Vorige periode" : "Previous period"}</div>
          <div className="text-2xl font-bold text-[#94A3B8] tabular-nums">{data.previous_total}</div>
          <div className="text-[10px] text-[#94A3B8] mt-0.5">{isNl ? `${data.window_days} dagen ervoor` : `${data.window_days} days before`}</div>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold">{isNl ? "Per dag" : "Per day avg"}</div>
          <div className="text-2xl font-bold text-[#0F172A] tabular-nums">{(data.total / Math.max(1, data.window_days)).toFixed(1)}</div>
          <div className="text-[10px] text-[#94A3B8] mt-0.5">{isNl ? "gemiddeld" : "average"}</div>
        </div>
      </div>

      {/* Sparkline */}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 mb-3">
        <div className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold mb-2">{isNl ? "Dagelijkse klikken" : "Daily clicks"}</div>
        <div className="flex items-end gap-[2px] h-16" data-testid={`sparkline-${slug}`}>
          {data.daily.map((d) => (
            <div
              key={d.date}
              className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-sm transition-colors min-h-[2px] relative group"
              style={{ height: `${(d.clicks / max) * 100}%` }}
            >
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#0F172A] text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {d.date}: {d.clicks}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Two-column: by source + top referrers */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold mb-2">{isNl ? "Per herkomst" : "By source"}</div>
          {data.by_source.length === 0 ? (
            <div className="text-xs text-[#94A3B8] italic">{isNl ? "Nog geen data" : "No data yet"}</div>
          ) : (
            <div className="space-y-1.5">
              {data.by_source.map((s) => {
                const lbl = SOURCE_LABELS[s.source]?.[isNl ? "nl" : "en"] || s.source;
                const pct = Math.round((s.clicks / data.total) * 100);
                return (
                  <div key={s.source} className="flex items-center gap-2 text-xs">
                    <div className="flex-1 truncate text-[#0F172A]">{lbl}</div>
                    <div className="w-16 bg-[#E2E8F0] rounded-full h-1.5 overflow-hidden">
                      <div className="bg-[#2563EB] h-full" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-10 text-right text-[#0F172A] font-bold tabular-nums">{s.clicks}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[10px] text-[#64748B] uppercase tracking-wider font-bold mb-2">{isNl ? "Top doorverwijspagina's" : "Top referring pages"}</div>
          {data.top_referrers.length === 0 ? (
            <div className="text-xs text-[#94A3B8] italic">{isNl ? "Nog geen data" : "No data yet"}</div>
          ) : (
            <div className="space-y-1.5">
              {data.top_referrers.slice(0, 6).map((r) => (
                <div key={r.page} className="flex items-center gap-2 text-xs">
                  <div className="flex-1 truncate text-[#0F172A] font-mono text-[10px]">{r.page}</div>
                  <div className="text-[#0F172A] font-bold tabular-nums">{r.clicks}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-[10px] text-[#94A3B8] mt-3 italic">
        {isNl
          ? "Klikken met UTM tagging. Controleer in je GA4: bron 'horseappcompare'."
          : "Clicks tracked with UTM tagging. Verify in your GA4 under source 'horseappcompare'."}
      </p>
    </div>
  );
};

// ---- Live preview side-panel — shows the actual visitor view in an iframe ----
const LivePreviewPane = null;  // removed Feb 2026 — iframe added clutter without value


// ---- Action Center — top-of-dashboard "command center" that aggregates ALL
// signals that need publisher attention into one priority-ranked feed.
// Powered by the single /api/publisher/action-center endpoint (one round-trip).

const ACTION_ICON = { freshness: RefreshCw, content: Sparkles, reviews: MessageSquare, clicks_drop: TrendingDown };
const ACTION_TONE = {
  1: { bar: "from-rose-500 to-orange-500", chip: "bg-rose-100 text-rose-700 border-rose-200", pill: "Urgent" },
  2: { bar: "from-violet-500 to-sky-500",  chip: "bg-violet-100 text-violet-700 border-violet-200", pill: "Important" },
  3: { bar: "from-slate-400 to-slate-500", chip: "bg-slate-100 text-slate-700 border-slate-200", pill: "Nice to have" },
};

const ActionCenter = ({ apps, isNl, apiClient, onEdit }) => {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  useEffect(() => {
    let cancelled = false;
    apiClient.get("/publisher/action-center")
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [apiClient]);

  if (items === null) return null; // initial loading — keep dashboard quiet
  if (items.length === 0) return null; // all clean → component hides itself

  const appBySlug = Object.fromEntries((apps || []).map((a) => [a.slug, a]));

  const handleAction = (item) => {
    if (item.cta_target === "reviews") {
      const el = document.querySelector('[data-testid="publisher-reviews-section"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (item.cta_target === "analytics") {
      const el = document.querySelector(`[data-testid="publisher-app-${item.slug}"] [data-testid^="click-analytics-"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else {
        const card = document.querySelector(`[data-testid="publisher-app-${item.slug}"]`);
        if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }
    // default "edit"
    const app = appBySlug[item.slug];
    if (app) onEdit(app);
  };

  const urgentCount = items.filter((i) => i.priority === 1).length;

  return (
    <div className="bg-gradient-to-br from-violet-50 via-white to-sky-50 border border-violet-200 rounded-2xl p-5 mb-6" data-testid="action-center">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-violet-500 text-white rounded-lg flex items-center justify-center">
            <Sparkles size={14} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-violet-700">{isNl ? "Action Center" : "Action Center"}</div>
            <div className="font-bold text-[#0F172A] text-sm md:text-base">
              {items.length === 1
                ? (isNl ? "1 actiepunt voor je listings" : "1 thing to act on")
                : (isNl ? `${items.length} actiepunten voor je listings` : `${items.length} things to act on`)}
            </div>
          </div>
        </div>
        {urgentCount > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-200 px-2 py-1 rounded-full" data-testid="action-center-urgent-count">
            ⏰ {urgentCount} {isNl ? "urgent" : "urgent"}
          </span>
        )}
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => {
          const Icon = ACTION_ICON[item.type] || Sparkles;
          const tone = ACTION_TONE[item.priority] || ACTION_TONE[2];
          const pct = item.type === "content" ? Math.max(0, Math.min(100, Math.round(item.score || 0))) : null;
          return (
            <div
              key={`${item.slug}-${item.type}-${idx}`}
              data-testid={`action-center-item-${item.type}-${item.slug}`}
              className="bg-white border border-violet-100 rounded-xl p-3 flex items-center gap-3 hover:border-violet-300 transition-colors"
            >
              <div
                className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: item.color || "#2563EB" }}
                aria-hidden
                title={item.app_name}
              >
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-bold text-[#0F172A] text-sm truncate max-w-[180px]" title={item.app_name}>{item.app_name}</div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${tone.chip}`}>{tone.pill}</span>
                  {item.type === "freshness" && (
                    <span className="text-[10px] font-mono text-[#64748B]">· {item.days_left}d left</span>
                  )}
                  {item.type === "clicks_drop" && (
                    <span className="text-[10px] font-mono text-rose-600">· {item.clicks_prev_7d}→{item.clicks_7d}</span>
                  )}
                  {item.type === "content" && pct !== null && (
                    <span className="text-[10px] font-mono text-[#64748B]">· {pct}/100</span>
                  )}
                  {item.type === "reviews" && (
                    <span className="text-[10px] font-mono text-[#64748B]">· {item.pending_count}</span>
                  )}
                </div>
                <div className="text-[12px] text-[#0F172A] mt-0.5 truncate" title={item.title}>{item.title}</div>
                {item.hint && <div className="text-[11px] text-[#64748B] truncate mt-0.5">↳ {item.hint}</div>}
                {item.type === "content" && pct !== null && (
                  <div className="mt-1.5 h-1 bg-violet-50 rounded-full overflow-hidden border border-violet-100" aria-hidden>
                    <div className={`h-full bg-gradient-to-r ${tone.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
              <button
                onClick={() => handleAction(item)}
                className="shrink-0 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                data-testid={`action-center-cta-${item.type}-${item.slug}`}
              >
                {item.cta_label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- Freshness widget — "Your NEW/UPDATED badge expires in X days" --------
// Self-service retention nudge: shows publishers exactly how long their
// freshness badge stays live on the public directory, with a 1-click edit
// shortcut so they can refresh their listing without leaving the dashboard.

const FreshnessWidget = ({ app, isNl, onEdit }) => {
  const windowDays = app.freshness_window_days || 28;
  const daysLeft = app.freshness_days_left ?? 0;
  const isNew = !!app.recently_listed;
  const isUpdated = !!app.recently_updated;
  const hasBadge = isNew || isUpdated;
  const pct = Math.max(0, Math.min(100, Math.round((daysLeft / windowDays) * 100)));

  // Colour scheme matches the public badge: green for NEW, sky for UPDATED,
  // soft red when no badge is active (signalling "your listing is going stale").
  const accent = isNew
    ? { bar: "bg-emerald-500", ring: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", iconBg: "bg-emerald-500" }
    : isUpdated
    ? { bar: "bg-sky-500", ring: "border-sky-200", bg: "bg-sky-50", text: "text-sky-700", iconBg: "bg-sky-500" }
    : { bar: "bg-red-400", ring: "border-red-200", bg: "bg-red-50", text: "text-red-700", iconBg: "bg-red-500" };

  const badgeLabel = isNew
    ? (isNl ? "NEW badge actief" : "NEW badge live")
    : isUpdated
    ? (isNl ? "UPDATED badge actief" : "UPDATED badge live")
    : (isNl ? "Geen badge actief" : "No badge live");

  const headline = isNew
    ? (isNl
        ? `Je NEW badge verloopt over ${daysLeft} ${daysLeft === 1 ? "dag" : "dagen"}`
        : `Your NEW badge expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`)
    : isUpdated
    ? (isNl
        ? `Je UPDATED badge verloopt over ${daysLeft} ${daysLeft === 1 ? "dag" : "dagen"}`
        : `Your UPDATED badge expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}`)
    : (isNl
        ? "Geen verse-badge meer — werk je listing bij om weer op te vallen"
        : "No freshness badge live — refresh your listing to stand out again");

  const sub = isNew
    ? (isNl
        ? "Houd je listing actueel: na vervallen verandert NEW automatisch in UPDATED zodra je iets wijzigt."
        : "Keep your listing current: after NEW expires it automatically becomes UPDATED the moment you edit something.")
    : isUpdated
    ? (isNl
        ? "Bewerk je beschrijving of upload nieuwe screenshots om de badge nog 4 weken te verlengen."
        : "Edit your description or upload new screenshots to extend the badge for another 4 weeks.")
    : (isNl
        ? "Een korte update van je beschrijving plaatst de UPDATED badge weer 4 weken lang op je listing."
        : "A small description tweak puts the UPDATED badge back on your listing for 4 weeks.");

  return (
    <div
      className={`mt-3 ${accent.bg} border ${accent.ring} rounded-xl p-4`}
      data-testid={`freshness-widget-${app.slug}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 shrink-0 ${accent.iconBg} text-white rounded-lg flex items-center justify-center`}>
          {isNew ? <Sparkles size={16} /> : isUpdated ? <RefreshCw size={16} /> : <Clock size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${accent.text}`}>{badgeLabel}</span>
          </div>
          <div className="font-bold text-[#0F172A] text-sm leading-tight" data-testid={`freshness-headline-${app.slug}`}>
            {headline}
          </div>
          <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{sub}</p>
          {hasBadge && (
            <div className="mt-3">
              <div className="h-1.5 bg-white rounded-full overflow-hidden border border-[#E2E8F0]">
                <div className={`h-full ${accent.bar} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
                <span>{isNl ? "Nu" : "Now"}</span>
                <span>{isNl ? `Vervalt in ${windowDays} dagen` : `Expires in ${windowDays}d`}</span>
              </div>
            </div>
          )}
        </div>
        <button
          onClick={onEdit}
          className="shrink-0 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5"
          data-testid={`freshness-edit-${app.slug}`}
        >
          <Edit3 size={12} /> {isNl ? "Bewerk nu" : "Refresh now"}
        </button>
      </div>
    </div>
  );
};

// ---- Content Health indicator — circular score + recommendations ----
const STATUS_STYLE = {
  good:            { bg: "bg-emerald-500", text: "text-emerald-700", ring: "ring-emerald-200", label_en: "Looking great", label_nl: "Ziet er top uit" },
  ok:              { bg: "bg-blue-500",    text: "text-blue-700",    ring: "ring-blue-200",    label_en: "Decent — small tweaks possible", label_nl: "Goed — kleine verbeteringen mogelijk" },
  "needs-upgrade": { bg: "bg-amber-500",   text: "text-amber-800",   ring: "ring-amber-200",   label_en: "Needs an upgrade",  label_nl: "Heeft upgrade nodig" },
  empty:           { bg: "bg-red-500",     text: "text-red-700",     ring: "ring-red-200",     label_en: "Nearly empty",     label_nl: "Bijna leeg" },
};

const ContentHealth = ({ slug, isNl }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get(`/publisher/apps/${slug}/content-quality`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [slug]);

  if (error) return null;
  if (!data) {
    return (
      <div className="border-t border-[#E2E8F0] pt-4 text-xs text-[#94A3B8]">
        <Loader2 size={12} className="animate-spin inline" /> {isNl ? "Content health laden…" : "Loading content health…"}
      </div>
    );
  }

  const style = STATUS_STYLE[data.status] || STATUS_STYLE.empty;
  const pct = Math.max(0, Math.min(100, data.score));
  // Circular progress: SVG ring 36x36, radius 14, circumference ~88
  const circ = 2 * Math.PI * 14;
  const dash = (pct / 100) * circ;

  return (
    <div className="border-t border-[#E2E8F0] pt-4" data-testid={`content-health-${slug}`}>
      <div className="flex items-start gap-4">
        <div className="relative shrink-0">
          <svg width="56" height="56" viewBox="0 0 36 36" className="transform -rotate-90">
            <circle cx="18" cy="18" r="14" fill="none" stroke="#E2E8F0" strokeWidth="3" />
            <circle cx="18" cy="18" r="14" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" className={style.text} />
          </svg>
          <div className={`absolute inset-0 flex flex-col items-center justify-center ${style.text}`}>
            <span className="text-sm font-bold tabular-nums leading-none">{pct}</span>
            <span className="text-[8px] uppercase tracking-widest font-bold opacity-70 leading-none mt-0.5">/100</span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <div className="font-bold text-sm text-[#0F172A]">
              {isNl ? "Content health" : "Content health"}
            </div>
            <span className={`text-[10px] uppercase tracking-widest font-bold ${style.text}`}>{isNl ? style.label_nl : style.label_en}</span>
          </div>
          {data.recommendations.length > 0 ? (
            <>
              <p className="text-[11px] text-[#64748B] mb-2 leading-snug">
                {isNl ? "Verbeter je app-pagina met deze stappen:" : "Improve your app page with these steps:"}
              </p>
              <ul className="space-y-1">
                {data.recommendations.slice(0, 3).map((r, i) => (
                  <li key={i} className="text-xs text-[#0F172A] flex items-start gap-1.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${style.bg} shrink-0 mt-1.5`} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-xs text-emerald-700 leading-snug">
              {isNl ? "✓ Je app-pagina is compleet en goed gevuld. Goed bezig!" : "✓ Your app page is complete and well-filled. Nice work!"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};




// ─────────────────────────── LogoField ───────────────────────────
// Combined preview thumbnail + upload button + URL field. Reuses the
// publisher upload endpoint (same R2 / local fallback as screenshots) so
// publishers can drag in a PNG/SVG and the URL gets filled automatically.
const LogoField = ({ slug, value, onChange, isNl }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const input = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const up = await publisherUploadImage(slug, file);
      onChange(up.url);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div data-testid="publisher-logo-field">
      <label className="overline block mb-1">{isNl ? "Logo" : "Logo"}</label>
      <div className="flex items-center gap-2">
        <div className="w-12 h-12 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={mediaUrl(value)} alt="logo" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={18} className="text-[#CBD5E1]" />
          )}
        </div>
        <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs cursor-pointer transition-colors shrink-0 ${uploading ? "bg-[#E2E8F0] text-[#94A3B8]" : "bg-[#0F172A] hover:bg-[#1E293B] text-white"}`}>
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? (isNl ? "Bezig…" : "Uploading…") : (isNl ? "Upload logo" : "Upload logo")}
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            className="hidden"
            disabled={uploading}
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="publisher-logo-upload"
          />
        </label>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[10px] text-[#94A3B8] hover:text-red-600 shrink-0"
            data-testid="publisher-logo-clear"
          >
            {isNl ? "Wissen" : "Clear"}
          </button>
        )}
      </div>
      <input
        className="hac-input mt-2 text-xs"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isNl ? "of plak een Logo URL" : "or paste a Logo URL"}
        data-testid="publisher-logo-url"
      />
      {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
    </div>
  );
};


// ───────────────────────── FeaturesEditor ─────────────────────────
// Renders the global feature taxonomy as grouped, tickable chips so the
// publisher can pick every module their app offers. Selected ids feed into
// the parent `form.features` array and end up powering category/filter SEO.
const FeaturesEditor = ({ value, onChange, isNl }) => {
  const [taxonomy, setTaxonomy] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    getFeatureTaxonomy()
      .then((data) => {
        if (!cancelled) setTaxonomy(data);
      })
      .catch((e) => !cancelled && setErr(e?.message || "Failed to load features"));
    return () => { cancelled = true; };
  }, []);

  const selected = new Set(value || []);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };

  if (err) return <p className="text-xs text-red-700">{err}</p>;
  if (!taxonomy) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Loader2 size={14} className="animate-spin" /> {isNl ? "Functionaliteiten laden…" : "Loading features…"}
      </div>
    );
  }

  const groups = taxonomy.categories || {};
  const labels = taxonomy.labels || {};

  return (
    <div className="space-y-4" data-testid="publisher-features-editor">
      <div className="text-[11px] text-[#64748B]">
        {isNl ? "Geselecteerd" : "Selected"}: <strong className="text-[#0F172A]">{selected.size}</strong>
      </div>
      {Object.entries(groups).map(([catKey, ids]) => (
        <div key={catKey} className="border border-[#E2E8F0] rounded-lg p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB] mb-2">{labels[catKey] || catKey}</div>
          <div className="flex flex-wrap gap-1.5">
            {ids.map((id) => {
              const lbl = labels[id] || id;
              const on = selected.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggle(id)}
                  data-testid={`feature-chip-${id}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#2563EB] hover:text-[#2563EB]"}`}
                >
                  {lbl}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};


// ─────────────────────── ScreenshotsManager ───────────────────────
// Inline gallery: list + upload + caption-edit + delete + reorder for the
// publisher's own app screenshots. Uses publisher-scoped media endpoints
// (`/api/publisher/apps/{slug}/media[…]`). No reliance on the admin
// flow — ownership is enforced backend-side.
const ScreenshotsManager = ({ slug, isNl, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileInput = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await publisherListMedia(slug);
      setItems(Array.isArray(data) ? data.filter((m) => m.type !== "video") : []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const up = await publisherUploadImage(slug, file);
      await publisherCreateMedia(slug, { type: "image", url: up.url, caption: "" });
      await reload();
      if (onChange) onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onCaption = async (id, caption) => {
    setItems((curr) => curr.map((m) => (m.id === id ? { ...m, caption } : m)));
    try { await publisherUpdateMedia(slug, id, { caption }); } catch { /* shown via reload */ }
  };

  const onDelete = async (id) => {
    if (!window.confirm(isNl ? "Deze screenshot verwijderen?" : "Delete this screenshot?")) return;
    try {
      await publisherDeleteMedia(slug, id);
      await reload();
      if (onChange) onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Delete failed");
    }
  };

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await publisherReorderMedia(slug, next.map((m) => m.id));
      if (onChange) onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Reorder failed");
      await reload();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Loader2 size={14} className="animate-spin" /> {isNl ? "Schermafbeeldingen laden…" : "Loading screenshots…"}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="publisher-screenshots-manager">
      {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      {items.length === 0 ? (
        <div className="border-2 border-dashed border-[#CBD5E1] rounded-lg p-6 text-center">
          <ImageIcon size={20} className="mx-auto text-[#94A3B8] mb-2" />
          <p className="text-xs text-[#64748B] mb-3">{isNl ? "Nog geen schermafbeeldingen. Voeg er minimaal 3 toe voor de beste resultaten." : "No screenshots yet. Add at least 3 for best results."}</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((m, i) => (
            <li key={m.id} className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden flex flex-col" data-testid={`screenshot-${m.id}`}>
              <div className="relative aspect-video bg-[#F1F5F9]">
                <img src={mediaUrl(m.url)} alt={m.caption || `screenshot ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">#{i + 1}</div>
              </div>
              <div className="p-2 space-y-2">
                <input
                  className="w-full text-xs px-2 py-1.5 border border-[#E2E8F0] rounded focus:outline-none focus:border-[#2563EB]"
                  placeholder={isNl ? "Onderschrift (optioneel)" : "Caption (optional)"}
                  value={m.caption || ""}
                  maxLength={200}
                  onChange={(e) => onCaption(m.id, e.target.value)}
                  data-testid={`screenshot-caption-${m.id}`}
                />
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-[10px] px-2 py-1 rounded border border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] disabled:opacity-30">←</button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-[10px] px-2 py-1 rounded border border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A] disabled:opacity-30">→</button>
                  </div>
                  <button type="button" onClick={() => onDelete(m.id)} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid={`screenshot-delete-${m.id}`}>
                    <Trash2 size={11} /> {isNl ? "Wissen" : "Delete"}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-3">
        <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm cursor-pointer transition-colors ${uploading ? "bg-[#E2E8F0] text-[#94A3B8]" : "bg-[#2563EB] hover:bg-[#1D4ED8] text-white"}`}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? (isNl ? "Bezig…" : "Uploading…") : (isNl ? "Upload screenshot" : "Upload screenshot")}
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            disabled={uploading || items.length >= 12}
            onChange={(e) => onUpload(e.target.files?.[0])}
            data-testid="publisher-screenshot-upload"
          />
        </label>
        <span className="text-[11px] text-[#94A3B8]">{items.length}/12 · PNG/JPG/WebP, max 8 MB</span>
      </div>
    </div>
  );
};
